/**
 * First-party acquisition attribution — client capture module for the
 * Advertising Optimization arche ("AdOpt").
 *
 * On first page load, captures how the visitor arrived (click ids, UTMs,
 * referrer, landing path, and for a Google ad click the campaign / ad ids the
 * campaign's finalUrlSuffix put on the URL — see adopt-landing-params.ts),
 * classifies the touch, persists first/last touch
 * locally, and POSTs new touches to /v1/adopt/touch. After sign-in,
 * identifyIfNeeded() links the anonymous visitor_id to the user via
 * POST /v1/adopt/identify (once per user per browser), carrying the local
 * first/last-touch snapshots as a recovery path in case the live touch
 * POST never landed.
 *
 * IDENTITY
 *   visitor_id 'fv_' + 16 hex, localStorage, generated once per browser.
 *   Session id is SHARED with the analytics tracker (same sessionStorage
 *   key) so the server-side identity-stitch sweep can join attribution
 *   touches to authenticated analytics events.
 *
 * CONSENT POSTURE (v1, US-market default)
 *   Capture runs unless the user has explicitly declined optional cookies
 *   (consent status 'essential') — in that case nothing is stored or sent,
 *   and any previously stored attribution state is purged. 'unset'
 *   (undecided) captures WITH the consent state recorded on every touch so
 *   policy can be tightened retroactively. This is deliberately different
 *   from analytics (which requires 'accepted'): ad-click attribution is
 *   how paid traffic is accounted for, and V1 campaigns are US-targeted.
 *   EEA consent gating lands with the consent infrastructure phase.
 *
 * UNLIKE analytics.ts there is no buffering: touches are rare (one per
 * qualifying page load), so each is a single POST with one retry.
 */

import { getConsent, subscribe as subscribeConsent } from '@/lib/cookie-consent';
import { attachCsrfHeader } from '@/lib/csrf';
import { sessionCredentials } from '@/lib/embed-document';
import { IS_COMPANY_PORTAL } from '@/lib/portal-host';
import {
  ADOPT_GOOGLE_ADS_ID_PATTERN,
  ADOPT_GOOGLE_LANDING_PARAMS,
} from '@/lib/adopt-landing-params';

// =============================================================================
// Storage keys + constants
// =============================================================================

const VISITOR_ID_KEY = 'fias_vid';
const FIRST_TOUCH_KEY = 'fias_first_touch';
const LAST_TOUCH_KEY = 'fias_last_touch';
const IDENTIFIED_PREFIX = 'fias_identified:';
const ANALYTICS_SESSION_KEY = 'fias_analytics_session_id';

const SEARCH_ENGINE_HOSTS = [
  'google.',
  'bing.com',
  'duckduckgo.com',
  'search.yahoo.',
  'ecosia.org',
  'baidu.com',
  'yandex.',
  'brave.com',
];

const PAID_UTM_MEDIUMS = new Set([
  'cpc',
  'ppc',
  'paid',
  'paid_social',
  'paidsearch',
  'display',
  'retargeting',
]);

export type TouchType = 'ad_click' | 'referral' | 'organic' | 'direct';

export interface StoredTouch {
  touchType: TouchType;
  occurredAt: string;
  /** Server-authoritative Fias click id (adopt_clicks.click_id) from `?fclid=`.
   *  Persisted so Guest Trial Mode can present it at mint even when the visitor
   *  navigated away from the landing URL before starting a trial. */
  fclid?: string;
  gclid?: string;
  /** Google's campaign id from the landing URL's `adopt_cid` (the campaign's
   *  finalUrlSuffix). Persisted beside the gclid so a Google arrival can be
   *  presented — and attributed to its campaign — after navigating away. */
  externalCampaignRef?: string;
  /** Google's ad id from the landing URL's `adopt_crid`. */
  externalCreativeRef?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  referrer?: string;
  landingPath?: string;
}

interface InitOptions {
  /** Base URL for the adopt endpoints. Defaults to '/v1'. */
  apiBase?: string;
}

// =============================================================================
// Module state
// =============================================================================

let apiBase = '/v1';
let initialized = false;

// =============================================================================
// Small storage helpers (localStorage can throw in private mode — every
// access is wrapped; attribution silently degrades rather than breaking
// the app)
// =============================================================================

function lsGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function lsRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function generateVisitorId(): string {
  const bytes = new Uint8Array(8);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 8; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return `fv_${hex}`;
}

/** Returns the durable visitor id, creating it on first use. */
export function getVisitorId(): string | null {
  const existing = lsGet(VISITOR_ID_KEY);
  if (existing && /^fv_[0-9a-f]{16}$/.test(existing)) return existing;
  const fresh = generateVisitorId();
  lsSet(VISITOR_ID_KEY, fresh);
  return lsGet(VISITOR_ID_KEY) === fresh ? fresh : null;
}

function readStoredTouch(key: string): StoredTouch | null {
  const raw = lsGet(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredTouch;
    if (!parsed.touchType || !parsed.occurredAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * The ad click this visitor arrived from, as Guest Trial Mode presents it at
 * mint (the click both attributes the trial and, under the strict gate,
 * permits it):
 *
 *   - `fias`   — a tracked-link click: our own server-issued click id (fclid).
 *   - `google` — a Google Ads click: Google's gclid, with Google's campaign and
 *                ad ids from the landing URL (`adopt_cid` / `adopt_crid`;
 *                null when the landing carried none, e.g. a campaign deployed
 *                before the suffix existed).
 *
 * Last-click: the last stored touch first, then the first touch, then the live
 * URL. Within one touch an fclid wins over a gclid — it is our own
 * server-authoritative click record.
 */
export type CapturedAdClick =
  | { kind: 'fias'; fclid: string }
  | { kind: 'google'; gclid: string; campaignRef: string | null; creativeRef: string | null };

function adClickOf(t: {
  fclid?: string;
  gclid?: string;
  externalCampaignRef?: string;
  externalCreativeRef?: string;
}): CapturedAdClick | null {
  if (t.fclid) return { kind: 'fias', fclid: t.fclid };
  if (t.gclid) {
    return {
      kind: 'google',
      gclid: t.gclid,
      campaignRef: t.externalCampaignRef ?? null,
      creativeRef: t.externalCreativeRef ?? null,
    };
  }
  return null;
}

export function getCapturedAdClick(): CapturedAdClick | null {
  const last = readStoredTouch(LAST_TOUCH_KEY);
  const fromLast = last ? adClickOf(last) : null;
  if (fromLast) return fromLast;
  const first = readStoredTouch(FIRST_TOUCH_KEY);
  const fromFirst = first ? adClickOf(first) : null;
  if (fromFirst) return fromFirst;
  if (typeof window !== 'undefined') {
    return adClickOf(captureParams(window.location.search));
  }
  return null;
}

// =============================================================================
// Touch classification
// =============================================================================

interface CapturedParams {
  fclid?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  externalCampaignRef?: string;
  externalCreativeRef?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
}

function captureParams(search: string): CapturedParams {
  const params = new URLSearchParams(search);
  const get = (k: string): string | undefined => {
    const v = params.get(k);
    return v && v.length > 0 ? v.slice(0, 255) : undefined;
  };
  // Google ids are digits only; anything else (a hand-edited or unsubstituted
  // `{campaignid}`) is dropped here rather than sent and rejected server-side.
  const googleId = (k: string): string | undefined => {
    const v = params.get(k);
    return v && ADOPT_GOOGLE_ADS_ID_PATTERN.test(v) ? v : undefined;
  };
  return {
    fclid: get('fclid'),
    gclid: get('gclid'),
    gbraid: get('gbraid'),
    wbraid: get('wbraid'),
    externalCampaignRef: googleId(ADOPT_GOOGLE_LANDING_PARAMS.campaignRef),
    externalCreativeRef: googleId(ADOPT_GOOGLE_LANDING_PARAMS.creativeRef),
    utmSource: get('utm_source'),
    utmMedium: get('utm_medium'),
    utmCampaign: get('utm_campaign'),
    utmTerm: get('utm_term'),
    utmContent: get('utm_content'),
  };
}

function isExternalReferrer(referrer: string): boolean {
  if (!referrer) return false;
  try {
    const ref = new URL(referrer);
    return ref.host !== window.location.host;
  } catch {
    return false;
  }
}

function isSearchEngineReferrer(referrer: string): boolean {
  try {
    const host = new URL(referrer).host.toLowerCase();
    return SEARCH_ENGINE_HOSTS.some((s) => host.includes(s));
  } catch {
    return false;
  }
}

function classifyTouch(p: CapturedParams, referrer: string): TouchType {
  if (p.fclid || p.gclid || p.gbraid || p.wbraid) return 'ad_click';
  if (p.utmMedium && PAID_UTM_MEDIUMS.has(p.utmMedium.toLowerCase())) return 'ad_click';
  if (isExternalReferrer(referrer)) {
    return isSearchEngineReferrer(referrer) ? 'organic' : 'referral';
  }
  return 'direct';
}

// =============================================================================
// Init — capture the landing touch
// =============================================================================

/**
 * Initialize attribution capture. Call once at app bootstrap, before React
 * renders (beside initTelemetry/initAnalytics in main.tsx). Safe to call
 * again (no-op).
 */
export function initAttribution(opts: InitOptions = {}): void {
  // Ad-click attribution belongs to fias.io's landing pages; a Company portal
  // refuses /v1/adopt (ADR-024), so there is nothing to record there.
  if (IS_COMPANY_PORTAL) return;
  apiBase = opts.apiBase ?? '/v1';
  if (initialized) return;
  initialized = true;

  if (typeof window === 'undefined') return;

  // Explicit decline of optional tracking: capture nothing, purge state.
  if (getConsent().status === 'essential') {
    purgeAttributionState();
    // If the user later accepts, start capturing from that point on.
    subscribeConsent((record) => {
      if (record.status === 'accepted') captureCurrentLanding();
    });
    return;
  }

  captureCurrentLanding();

  subscribeConsent((record) => {
    if (record.status === 'essential') purgeAttributionState();
  });
}

function purgeAttributionState(): void {
  lsRemove(VISITOR_ID_KEY);
  lsRemove(FIRST_TOUCH_KEY);
  lsRemove(LAST_TOUCH_KEY);
}

function captureCurrentLanding(): void {
  const params = captureParams(window.location.search);
  const referrer = typeof document !== 'undefined' ? document.referrer : '';
  const touchType = classifyTouch(params, referrer);

  const isAttributionRelevant =
    touchType === 'ad_click' ||
    touchType === 'referral' ||
    touchType === 'organic' ||
    Object.values(params).some((v) => v !== undefined);

  // Plain internal reload / direct revisit with no new signal: nothing to
  // record — a direct load never overwrites last touch.
  if (!isAttributionRelevant) {
    // Still make sure the visitor id exists so identify can link later
    // journeys even for first-visit-direct users.
    getVisitorId();
    return;
  }

  const touch: StoredTouch = {
    touchType,
    occurredAt: new Date().toISOString(),
    fclid: params.fclid,
    gclid: params.gclid,
    externalCampaignRef: params.externalCampaignRef,
    externalCreativeRef: params.externalCreativeRef,
    utmSource: params.utmSource,
    utmMedium: params.utmMedium,
    utmCampaign: params.utmCampaign,
    utmTerm: params.utmTerm,
    utmContent: params.utmContent,
    referrer: referrer ? referrer.slice(0, 1024) : undefined,
    landingPath: window.location.pathname.slice(0, 512),
  };

  // First touch is write-once; last touch overwrites on every new signal.
  if (!readStoredTouch(FIRST_TOUCH_KEY)) {
    lsSet(FIRST_TOUCH_KEY, JSON.stringify(touch));
  }
  lsSet(LAST_TOUCH_KEY, JSON.stringify(touch));

  void postTouch(params, touch);
}

async function postTouch(params: CapturedParams, touch: StoredTouch): Promise<void> {
  const visitorId = getVisitorId();
  if (!visitorId) return;

  let sessionId: string | undefined;
  try {
    sessionId = sessionStorage.getItem(ANALYTICS_SESSION_KEY) ?? undefined;
  } catch {
    sessionId = undefined;
  }

  const body = JSON.stringify({
    clientTouchId: generateUuid(),
    visitorId,
    ...(sessionId ? { sessionId } : {}),
    ...(params.fclid ? { fclid: params.fclid } : {}),
    ...(touch.gclid ? { gclid: touch.gclid } : {}),
    ...(params.gbraid ? { gbraid: params.gbraid } : {}),
    ...(params.wbraid ? { wbraid: params.wbraid } : {}),
    ...(touch.externalCampaignRef ? { externalCampaignRef: touch.externalCampaignRef } : {}),
    ...(touch.externalCreativeRef ? { externalCreativeRef: touch.externalCreativeRef } : {}),
    ...(touch.utmSource ? { utmSource: touch.utmSource } : {}),
    ...(touch.utmMedium ? { utmMedium: touch.utmMedium } : {}),
    ...(touch.utmCampaign ? { utmCampaign: touch.utmCampaign } : {}),
    ...(touch.utmTerm ? { utmTerm: touch.utmTerm } : {}),
    ...(touch.utmContent ? { utmContent: touch.utmContent } : {}),
    ...(touch.referrer ? { referrer: touch.referrer } : {}),
    ...(touch.landingPath ? { landingPath: touch.landingPath } : {}),
    touchType: touch.touchType,
    consentStatus: getConsent().status,
    occurredAt: touch.occurredAt,
  });

  // Sent WITHOUT the session cookie. The touch route never acts as the user —
  // it records an anonymous, visitor-keyed row; the user link comes later
  // from `identify` — so the cookie bought nothing but a CSRF check this
  // request cannot pass: it runs at bootstrap, before AuthContext has fetched
  // /auth/me, and the token (on the API origin, unreadable here) is only
  // cached from that response. A signed-in visitor's touch 403'd on its first
  // attempt every time. Cross-site forgery is still refused by the route's
  // Origin allowlist; `identify` below keeps the cookie and the header.
  const send = () =>
    fetch(`${apiBase}/adopt/touch`, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body,
    });

  try {
    const res = await send();
    // Only a server failure is worth one more try. A 403 is the Origin
    // allowlist, which a retry cannot change.
    if (res.status >= 500) {
      // One delayed retry; beyond that the identify-time recovery path
      // and the stored local snapshot carry the data.
      setTimeout(() => void send().catch(() => undefined), 10_000);
    }
  } catch {
    setTimeout(() => void send().catch(() => undefined), 10_000);
  }
}

// =============================================================================
// Identify — visitor → user stitch after auth
// =============================================================================

/**
 * Links the current visitor to the signed-in user. Call whenever the auth
 * context resolves a user (AuthContext effect). Guarded per (user, browser)
 * by a localStorage flag; retries on next call if the POST fails.
 */
export async function identifyIfNeeded(userId: string): Promise<void> {
  if (typeof window === 'undefined' || !userId || IS_COMPANY_PORTAL) return;
  if (getConsent().status === 'essential') return;

  const flagKey = `${IDENTIFIED_PREFIX}${userId}`;
  if (lsGet(flagKey) === 'true') return;

  const visitorId = getVisitorId();
  if (!visitorId) return;

  const firstTouch = readStoredTouch(FIRST_TOUCH_KEY);
  const lastTouch = readStoredTouch(LAST_TOUCH_KEY);

  try {
    // identify is requireAuth, so the session cookie is ALWAYS present and the
    // CSRF check ALWAYS fires — without this header it 403s every time, and the
    // visitor→user stitch never happens.
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    attachCsrfHeader('POST', headers);
    const res = await fetch(`${apiBase}/adopt/identify`, {
      method: 'POST',
      credentials: sessionCredentials(),
      headers,
      body: JSON.stringify({
        visitorId,
        ...(firstTouch ? { firstTouch } : {}),
        ...(lastTouch ? { lastTouch } : {}),
      }),
    });
    if (res.ok) {
      lsSet(flagKey, 'true');
    }
    // Non-2xx: leave the flag unset — the next authenticated load retries,
    // and the server-side session-stitch sweep is the final backstop.
  } catch {
    // Network error: same retry-on-next-load behavior.
  }
}

// =============================================================================
// Test helpers
// =============================================================================

export function _resetAttributionForTests(): void {
  initialized = false;
  apiBase = '/v1';
  purgeAttributionState();
}
